import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from '@nextui-org/react';
import { apiFetch } from '../lib/api';
import { trackingText } from '../lib/trackingI18n';
import { useAuth } from '../hooks/useAuth';
import { TRACKING_EXTENSION_ID, detectExtension, readTikTokSession } from '../lib/trackingExtension';

// Apartado Tracking: sesiones de TikTok con las que el sondeo de agencias
// pregunta a EulerStream por 50 creadores de golpe (bulk_live_check exige
// sesión de TikTok). Varias cuentas de respaldo; el cron rota si una caduca.

const STATE_LABEL = {
  ok: ['Sana', 'success'],
  untested: ['Sin probar', 'default'],
  expired: ['Caducada', 'danger'],
  error: ['Con errores', 'warning'],
  paused: ['Pausada', 'default']
};

const TIKTOK_LOGIN = 'https://www.tiktok.com/login';
// Zip de la extensión, generado en cada build por scripts/pack-extension.mjs
// (queda en public/ y Vite lo sirve en la raíz del sitio).
const EXTENSION_ZIP_URL = '/tikcontrol-tracking-extension.zip';

function formatTs(n) {
  if (!n) return '—';
  const d = new Date(n);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function formatDate(n) {
  if (!n) return '—';
  const d = new Date(n);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function extensionErrorText(error) {
  if (error === 'no_session') return 'No hay sesión de TikTok en este navegador: inicia sesión en tiktok.com y vuelve a pulsar.';
  if (error === 'not_installed' || error === 'no_chrome') return 'No se detecta la extensión TikControl Tracking en este navegador.';
  if (error === 'timeout') return 'La extensión no ha respondido a tiempo. Recárgala en chrome://extensions y prueba otra vez.';
  if (error === 'origin_not_allowed') return 'La extensión no acepta este origen. Abre el backoffice desde backoffice.tikcontrol.live.';
  return `La extensión no ha devuelto la sesión (${error || 'sin detalle'}).`;
}

function testResultText(test) {
  if (!test) return 'Guardada.';
  if (test.outcome === 'ok') {
    return `Guardada y probada: EulerStream responde (${test.checked} creador(es) comprobados, ${test.live} en directo).`;
  }
  if (test.outcome === 'auth') return `Guardada, pero EulerStream rechaza la sesión: ${test.detail}`;
  return `Guardada, pero la prueba ha fallado (${test.detail}). Se reintentará; si sigue así, prueba otra vez desde la tabla.`;
}

export default function Tracking() {
  const { canWrite } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [ext, setExt] = useState({ checked: false, installed: false, version: '' });
  const [busy, setBusy] = useState('');
  const [manual, setManual] = useState({ open: false, sessionId: '', ttTargetIdc: '', label: '' });

  const load = useCallback(async () => {
    setError(null);
    try {
      const [list, st] = await Promise.all([
        apiFetch('/api/backoffice/tracking/sessions'),
        apiFetch('/api/backoffice/tracking/status')
      ]);
      setSessions(list.sessions || []);
      setSummary(list.summary || null);
      setStatus(st || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkExtension = useCallback(async () => {
    const r = await detectExtension();
    setExt({ checked: true, installed: r.installed, version: r.version });
  }, []);

  useEffect(() => {
    load();
    checkExtension();
  }, [load, checkExtension]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(t);
  }, [toast]);

  async function saveSession(payload) {
    const out = await apiFetch('/api/backoffice/tracking/sessions', { method: 'POST', body: payload });
    setToast({ type: out.test?.outcome === 'ok' ? 'ok' : 'warn', msg: testResultText(out.test) });
    await load();
  }

  async function connectOAuth() {
    setBusy('oauth');
    try {
      const out = await apiFetch('/api/backoffice/tracking/oauth/start', { method: 'POST' });
      const destination = new URL(out.authorizationUrl);
      if (destination.origin !== 'https://www.eulerstream.com') throw new Error('Invalid OAuth destination');
      window.location.assign(destination.href);
    } catch (e) {
      setError(e.message);
      setBusy('');
    }
  }

  async function saveFromExtension() {
    setBusy('save');
    try {
      const r = await readTikTokSession();
      if (!r.ok) {
        setToast({ type: 'err', msg: extensionErrorText(r.error) });
        if (r.error === 'not_installed' || r.error === 'no_chrome') setExt({ checked: true, installed: false, version: '' });
        return;
      }
      await saveSession({ ...r.session, ...(r.account || {}) });
    } catch (e) {
      setToast({ type: 'err', msg: e.message });
    } finally {
      setBusy('');
    }
  }

  async function saveManual() {
    setBusy('save');
    try {
      await saveSession({ sessionId: manual.sessionId, ttTargetIdc: manual.ttTargetIdc, label: manual.label });
      setManual({ open: false, sessionId: '', ttTargetIdc: '', label: '' });
    } catch (e) {
      setToast({ type: 'err', msg: e.message });
    } finally {
      setBusy('');
    }
  }

  async function testOne(id) {
    setBusy(`test:${id}`);
    try {
      const out = await apiFetch(`/api/backoffice/tracking/sessions/${id}/test`, { method: 'POST' });
      setToast({ type: out.outcome === 'ok' ? 'ok' : 'warn', msg: testResultText(out) });
      await load();
    } catch (e) {
      setToast({ type: 'err', msg: e.message });
    } finally {
      setBusy('');
    }
  }

  async function removeOne(session) {
    const who = session.username ? `@${session.username}` : session.label || session.id;
    if (!window.confirm(`¿Eliminar la sesión ${who} del tracking?`)) return;
    setBusy(`delete:${session.id}`);
    try {
      await apiFetch(`/api/backoffice/tracking/sessions/${session.id}`, { method: 'DELETE' });
      setToast({ type: 'ok', msg: `Sesión ${who} eliminada.` });
      await load();
    } catch (e) {
      setToast({ type: 'err', msg: e.message });
    } finally {
      setBusy('');
    }
  }

  const healthy = summary?.healthy || 0;
  const sweep = status?.lastSweep || null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Tracking</h1>
          <p className="text-sm text-default-500">
            Sesiones de TikTok con las que el sondeo de agencias pregunta a EulerStream por 50
            creadores de golpe. Guarda varias cuentas de respaldo: si una caduca, el sondeo pasa a
            la siguiente y avisa por correo; sin ninguna sana, vuelve al sondeo uno a uno.
          </p>
        </div>
        {status?.oauth?.available ? <Chip color="success" variant="flat">{trackingText('ready')}</Chip> : summary ? (
          <Chip color={healthy > 0 ? 'success' : 'warning'} variant="flat">
            {healthy > 0 ? `Sondeo en bloque activo · ${healthy} sesión(es) sana(s)` : 'Sondeo uno a uno · sin sesiones sanas'}
          </Chip>
        ) : null}
      </div>

      <div className="flex items-center gap-3">
        {canWrite && <Button onPress={connectOAuth} isLoading={busy === 'oauth'} isDisabled={!!busy}>{trackingText('connect')}</Button>}
        {!status?.oauth?.available && <span>{trackingText('authorize')}</span>}
      </div>

      {toast && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            toast.type === 'ok'
              ? 'bg-success-50 text-success-700'
              : toast.type === 'warn'
                ? 'bg-warning-50 text-warning-700'
                : 'bg-danger-50 text-danger-700'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">{error}</div>
      )}

      {loading ? (
        <Spinner className="mt-8 self-center" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(300px,380px)_1fr]">
          <div className="flex flex-col gap-4">
            <Card className="h-fit">
              <CardHeader className="pb-0">
                <span className="text-base font-semibold">Añadir cuenta</span>
              </CardHeader>
              <CardBody className="flex flex-col gap-3 text-sm">
                {!canWrite ? (
                  <p className="text-default-500">Solo los administradores con permiso de escritura pueden añadir sesiones.</p>
                ) : ext.installed ? (
                  <>
                    <p className="text-default-500">
                      Extensión detectada (v{ext.version}). Inicia sesión en tiktok.com con la cuenta que quieras
                      usar para el tracking y pulsa guardar. No cierres sesión después: cerrar la pestaña basta.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="flat" onPress={() => window.open(TIKTOK_LOGIN, '_blank', 'noopener')}>
                        1. Abrir tiktok.com
                      </Button>
                      <Button size="sm" color="primary" isLoading={busy === 'save'} onPress={saveFromExtension}>
                        2. Guardar sesión de TikTok
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-default-500">
                      No se detecta la extensión <b>TikControl Tracking</b> en este navegador. Se instala una vez
                      por PC:
                    </p>
                    <ol className="list-decimal pl-5 text-default-500">
                      <li>Descarga el zip y descomprímelo en una carpeta que no vayas a borrar (Chrome la lee desde ahí).</li>
                      <li>Abre <code>chrome://extensions</code> y activa el modo de desarrollador.</li>
                      <li>Pulsa "Cargar descomprimida" y elige la carpeta <code>tikcontrol-tracking</code> descomprimida.</li>
                      <li>Comprueba que el ID es <code>{TRACKING_EXTENSION_ID}</code> y pulsa "Volver a comprobar".</li>
                    </ol>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        color="primary"
                        as="a"
                        href={EXTENSION_ZIP_URL}
                        download="tikcontrol-tracking-extension.zip"
                      >
                        Descargar extensión (.zip)
                      </Button>
                      <Button size="sm" variant="flat" onPress={checkExtension}>Volver a comprobar</Button>
                      <Button size="sm" variant="light" onPress={() => setManual((m) => ({ ...m, open: !m.open }))}>
                        {manual.open ? 'Ocultar formulario manual' : 'Pegar las cookies a mano'}
                      </Button>
                    </div>
                  </>
                )}

                {canWrite && manual.open ? (
                  <div className="flex flex-col gap-2 rounded-md bg-default-50 p-3">
                    <p className="text-default-500">
                      Con la cuenta logueada en tiktok.com, abre las herramientas de desarrollador → Aplicación →
                      Cookies → tiktok.com y copia los valores de <code>sessionid</code> y <code>tt-target-idc</code>.
                    </p>
                    <Input
                      size="sm"
                      label="sessionid"
                      value={manual.sessionId}
                      onValueChange={(v) => setManual((m) => ({ ...m, sessionId: v }))}
                    />
                    <Input
                      size="sm"
                      label="tt-target-idc"
                      value={manual.ttTargetIdc}
                      onValueChange={(v) => setManual((m) => ({ ...m, ttTargetIdc: v }))}
                    />
                    <Input
                      size="sm"
                      label="Etiqueta (p. ej. @cuenta)"
                      value={manual.label}
                      onValueChange={(v) => setManual((m) => ({ ...m, label: v }))}
                    />
                    <Button
                      size="sm"
                      color="primary"
                      isDisabled={!manual.sessionId.trim()}
                      isLoading={busy === 'save'}
                      onPress={saveManual}
                    >
                      Guardar sesión
                    </Button>
                  </div>
                ) : null}
              </CardBody>
            </Card>

            <Card className="h-fit">
              <CardHeader className="pb-0">
                <span className="text-base font-semibold">Última pasada del sondeo</span>
              </CardHeader>
              <CardBody className="text-sm text-default-500">
                {sweep ? (
                  <ul className="flex flex-col gap-1">
                    <li>Cuándo: {formatTs(sweep.at)}</li>
                    <li>Modo: {sweep.mode === 'bulk' ? 'en bloque (sesión de TikTok)' : 'uno a uno'}</li>
                    <li>Creadores comprobados: {sweep.creators ?? 0} · en directo: {sweep.live ?? 0}</li>
                    <li>Llamadas a EulerStream: {sweep.calls ?? 0} · errores: {sweep.errors ?? 0}</li>
                    {sweep.unknown ? <li>Sin respuesta concluyente: {sweep.unknown}</li> : null}
                    {sweep.reason ? <li>Nota: {sweep.reason}</li> : null}
                  </ul>
                ) : (
                  <p>Todavía no hay ninguna pasada registrada.</p>
                )}
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex items-center justify-between pb-0">
              <span className="text-base font-semibold">Sesiones guardadas</span>
              <Button size="sm" variant="flat" onPress={load}>Actualizar</Button>
            </CardHeader>
            <CardBody>
              <Table aria-label="Sesiones de TikTok del tracking" removeWrapper>
                <TableHeader>
                  <TableColumn>Cuenta</TableColumn>
                  <TableColumn>Estado</TableColumn>
                  <TableColumn>Caduca</TableColumn>
                  <TableColumn>Último uso</TableColumn>
                  <TableColumn>Usos</TableColumn>
                  <TableColumn>Último error</TableColumn>
                  <TableColumn>Acciones</TableColumn>
                </TableHeader>
                <TableBody emptyContent="Sin sesiones. Añade la primera desde el panel de la izquierda.">
                  {sessions.map((s) => {
                    const [label, color] = STATE_LABEL[s.state] || STATE_LABEL.untested;
                    return (
                      <TableRow key={s.id}>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{s.username ? `@${s.username}` : s.label || s.id}</span>
                            <span className="text-xs text-default-400">
                              {s.label && s.username && s.label !== `@${s.username}` ? `${s.label} · ` : ''}
                              añadida {formatDate(s.addedAt)}{s.addedBy ? ` por ${s.addedBy}` : ''}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Chip size="sm" color={color} variant="flat">{label}</Chip>
                        </TableCell>
                        <TableCell>{formatDate(s.expiresAt)}</TableCell>
                        <TableCell>{formatTs(s.lastUsedAt)}</TableCell>
                        <TableCell>{s.uses}</TableCell>
                        <TableCell>
                          <span className="text-xs text-default-500">{s.lastError || '—'}</span>
                        </TableCell>
                        <TableCell>
                          {canWrite ? (
                            <div className="flex gap-2">
                              <Button size="sm" variant="flat" isLoading={busy === `test:${s.id}`} onPress={() => testOne(s.id)}>
                                Probar
                              </Button>
                              <Button size="sm" color="danger" variant="light" isLoading={busy === `delete:${s.id}`} onPress={() => removeOne(s)}>
                                Eliminar
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-default-400">Solo lectura</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}
